import React, { useState, useRef, useEffect } from "react";
import { FaRegThumbsUp, FaRegCommentAlt, FaShare } from "react-icons/fa";

/**
 * PostActionBar
 * Props:
 *  - post: the post object (expects post.postId, optional post.reactionCounts, optional post.userReaction)
 *  - onOpenComments: function to call when user clicks Comment (optional)
 *  - onShare: function to call when user clicks Share (optional)
 *  - onReact: function(postId, reaction) -> called when user picks a reaction (optional; used for backend)
 *
 * Reaction names used: "like", "love", "care", "haha", "wow", "sad", "angry"
 *
 * This component is self-contained and uses optimistic local state updates.
 */
const REACTIONS = [
  { key: "like", emoji: "👍", label: "Like" },
  { key: "love", emoji: "❤️", label: "Love" },
  { key: "care", emoji: "🫶", label: "Care" },
  { key: "haha", emoji: "😆", label: "Haha" },
  { key: "wow", emoji: "😮", label: "Wow" },
  { key: "sad", emoji: "😢", label: "Sad" },
  { key: "angry", emoji: "😡", label: "Angry" },
];

const PostActionBar = ({ post, onOpenComments, onShare, onReact }) => {
  const [counts, setCounts] = useState(() => {
    // safe init: reactionCounts might be missing from backend
    // expected shape: { like: 2, love: 1, ... }
    return { ...(post?.reactionCounts || {}) };
  });

  const [userReaction, setUserReaction] = useState(post?.userReaction || null);
  const [reactionsOpen, setReactionsOpen] = useState(false);
  const hoverTimer = useRef(null);
  const popRef = useRef(null);
  const likeBtnRef = useRef(null);

// Recursive helper to count a comment and all its nested replies
const countCommentAndReplies = (comment) => {
  if (!comment) return 0;
  const repliesCount = Array.isArray(comment.replies)
    ? comment.replies.reduce((sum, r) => sum + countCommentAndReplies(r), 0)
    : 0;
  return 1 + repliesCount; // 1 for the comment itself + all nested replies
};

// Compute total comments
const totalComments = Array.isArray(post?.comments)
  ? post.comments.reduce((sum, c) => sum + countCommentAndReplies(c), 0)
  : 0;


  // derived total reaction count
  const totalReactions = Object.values(counts).reduce(
    (s, v) => s + (Number(v) || 0),
    0
  );
  
  

  // When clicking a reaction (or toggling off)
  const handleSelectReaction = (reactionKey) => {
    // Optimistic local update
    setCounts((prev) => {
      const next = { ...prev };
      // if user already reacted with this reaction -> remove it
      if (userReaction === reactionKey) {
        next[reactionKey] = Math.max(0, (next[reactionKey] || 0) - 1);
      } else {
        // remove previous reaction count
        if (userReaction) {
          next[userReaction] = Math.max(0, (next[userReaction] || 0) - 1);
        }
        // add new reaction
        next[reactionKey] = (next[reactionKey] || 0) + 1;
      }
      return next;
    });

    const newReaction = userReaction === reactionKey ? null : reactionKey;
    setUserReaction(newReaction);
    setReactionsOpen(false);

    // Fire external handler to persist to backend (if provided)
    if (typeof onReact === "function") {
      try {
        onReact(post.postId, newReaction);
      } catch (err) {
        // swallow errors from optional callback
        console.error("onReact callback error:", err);
      }
    }
  };

  // Hover logic: open reactions popup after short delay on hover
  const openWithDelay = () => {
    clearTimeout(hoverTimer.current);
    hoverTimer.current = setTimeout(() => setReactionsOpen(true), 220);
  };
  const closeWithDelay = () => {
    clearTimeout(hoverTimer.current);
    hoverTimer.current = setTimeout(() => setReactionsOpen(false), 150);
  };

  // Close popup when clicking outside
  useEffect(() => {
    const onDocClick = (e) => {
      if (
        reactionsOpen &&
        popRef.current &&
        !popRef.current.contains(e.target) &&
        likeBtnRef.current &&
        !likeBtnRef.current.contains(e.target)
      ) {
        setReactionsOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [reactionsOpen]);

  // small helper to render the primary reaction icon (if reacted) else thumbs up outline
  const PrimaryReactionIcon = () => {
    if (!userReaction) return <FaRegThumbsUp size={16} />;
    const r = REACTIONS.find((x) => x.key === userReaction);
    return (
      <span
        aria-hidden
        style={{
          fontSize: 16,
          lineHeight: "16px",
          display: "inline-block",
          transform: "translateY(-1px)",
        }}
      >
        {r?.emoji || "👍"}
      </span>
    );
  };

 return (
  <>
    <style>{`
      .fb-action-bar {
        display:flex;
        align-items:center;
        justify-content:flex-start; /* IMPORTANT */
        color:#65676b;
        font-size:0.95rem;
        user-select:none;
      }
    .fb-action-left {
      display:flex;
      align-items:center;
     
    }

    .fb-action-right {
      display:flex;
      align-items:center;
      gap:10px;
      margin-right:auto; /* forces right alignment */
    }

      .fb-action-btn {
        display:flex;
        align-items:center;
        gap:8px;
        padding:8px 12px;
        border-radius:8px;
        cursor:pointer;
        transition:background .12s ease;
      }
      .fb-reaction-popup {
        position:absolute;
        z-index:1200;
        display:flex;
        align-items:center;
        gap:8px;
        padding:8px 10px;
        background:#ffffff;
        border-radius:28px;
        box-shadow: 0 6px 18px rgba(0,0,0,0.12);
        transform-origin: bottom center;
        animation: popIn .09s ease-out;
        -webkit-user-select:none;
      }
      @keyframes popIn {
        from { transform: translateY(6px) scale(.96); opacity:0; }
        to   { transform: translateY(0) scale(1); opacity:1; }
      }
      .fb-reaction-pill {
        width:36px;
        height:36px;
        display:flex;
        align-items:center;
        justify-content:center;
        border-radius:50%;
        font-size:20px;
        transition: transform .09s ease;
      }
      .fb-reaction-pill:hover { transform: translateY(-6px) scale(1.12); }
      .fb-comment-count {
        display:inline-block;
        font-weight:500;
        color:#050505;
      }
      .fb-floating-count { pointer-events: none; }
    `}</style>

<div className="fb-action-bar" style={{ position: "relative", width: "100%", display: "flex", alignItems: "center" }}>

  {/* LEFT GROUP: Like + Comment */}
  <div style={{ marginLeft: "10px", display: "flex", alignItems: "center", }}>
    <div
      className="fb-action-btn"
      ref={likeBtnRef}
      onMouseEnter={openWithDelay}
      onMouseLeave={closeWithDelay}
      onClick={() => handleSelectReaction("like")}
      role="button"
      aria-label="Like"
      style={{ display: "flex", alignItems: "center", gap: 4, cursor: "pointer", position: "relative" }}
    >
      <PrimaryReactionIcon size={16} />
      {totalReactions > 0 && (
        <span style={{ fontSize: "0.8rem", fontWeight: 600 }}>
          {totalReactions}
        </span>
      )}

      {reactionsOpen && (
        <div
          ref={popRef}
          className="fb-reaction-popup"
        style={{
  bottom: "54px",
  left:
    likeBtnRef.current?.getBoundingClientRect().left < 120
      ? "0"
      : "50%",
  transform:
    likeBtnRef.current?.getBoundingClientRect().left < 120
      ? "translateX(0)"
      : "translateX(-50%)",
}}

        >
          {REACTIONS.map((r) => (
            <div
              key={r.key}
              className="fb-reaction-pill"
              onClick={(e) => {
                e.stopPropagation();
                handleSelectReaction(r.key);
              }}
              role="button"
              title={r.label}
            >
              <span style={{ fontSize: 20 }}>{r.emoji}</span>
            </div>
          ))}
        </div>
      )}
    </div>

    <div
      className="fb-action-btn"
      onClick={() => onOpenComments?.(post.postId)}
      role="button"
      aria-label="Open comments"
      style={{ display: "flex", alignItems: "center", gap: 4, cursor: "pointer" }}
    >
      <FaRegCommentAlt size={16} />
      {totalComments > 0 && (
        <span style={{ fontSize: "0.8rem", fontWeight: 600 }}>
          {totalComments}
        </span>
      )}
    </div>
  </div>

  {/* SPACER */}
  <div style={{ flex: .95 }} />

  {/* RIGHT GROUP: Reaction Emojis */}
  <div style={{ display: "flex", alignItems: "center", }}>
    {totalReactions > 0 && (
      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
        {Object.entries(counts)
          .filter(([_, v]) => v > 0)
          .slice(0, 5)
          .map(([key]) => {
            const r = REACTIONS.find((x) => x.key === key);
            return (
              <span key={key} style={{ fontSize: 16 }}>
                {r?.emoji}
              </span>
            );
          })}
      </div>
    )}
  </div>
</div>

  </>
);

};

export default PostActionBar;
